import { captureException, setTag } from "@sentry/nextjs";

import { normalizeUrl } from "./utils/normalize-url";

interface ReportErrorToSentryOptions {
  endpoint?: string;
  error: Error;
  errorMessage?: string;
  errorType: string;
  method?: string;
  normalizedEndpoint?: string;
  status?: number;
}

/**
 * Reports an error to Sentry with appropriate tags and fingerprinting
 */
function reportErrorToSentry({
  error,
  endpoint = "",
  method = "GET",
  errorType,
  status,
  errorMessage,
  normalizedEndpoint,
}: ReportErrorToSentryOptions) {
  setTag("endpoint", endpoint);
  setTag("normalized_endpoint", normalizedEndpoint);
  setTag("http_method", method);
  setTag("error_type", errorType);
  setTag("status_code", status);

  const fingerprint = [
    "{{ default }}",
    normalizedEndpoint || "unknown-endpoint",
    method || "unknown-method",
    errorType,
    errorMessage || "unknown-error-message",
  ];

  captureException(error, { fingerprint });
}

export interface HTTPErrorDetails {
  endpoint?: string;
  errorMessage?: string;
  method?: string;
  responseText?: string;
  status?: number;
}

export class HttpError extends Error {
  endpoint?: string;
  method?: string;
  status?: number;
  errorMessage?: string;

  constructor(
    message: string,
    endpoint?: string,
    method?: string,
    status?: number,
    errorMessage?: string,
  ) {
    super(message);
    this.name = "HttpError";
    this.endpoint = endpoint;
    this.method = method;
    this.status = status;
    this.errorMessage = errorMessage;
  }
}

export class RequestError extends HttpError {
  constructor(
    message: string,
    endpoint?: string,
    method?: string,
    status?: number,
    errorMessage?: string,
  ) {
    super(message, endpoint, method, status, errorMessage);
    this.name = "RequestError";
  }
}

export class ResponseError extends HttpError {
  constructor(
    message: string,
    endpoint?: string,
    method?: string,
    status?: number,
    errorMessage?: string,
  ) {
    super(message, endpoint, method, status, errorMessage);
    this.name = "ResponseError";
  }
}

// For network errors like connection failures
export class NetworkError extends HttpError {
  originalError: Error;

  constructor(
    message: string,
    originalError: Error,
    details: HTTPErrorDetails,
  ) {
    super(message, details.endpoint, details.method);
    this.name = "NetworkError";
    this.originalError = originalError;
  }
}

// For unknown errors
export class UnknownError extends HttpError {
  originalError: Error;

  constructor(originalError: Error, endpoint?: string, method?: string) {
    super(originalError.message, endpoint, method);
    this.name = "UnknownError";
    this.originalError = originalError;
  }
}

type RequestHeaders =
  | NonNullable<RequestInit["headers"]>
  | Record<string, string | undefined>;

export interface RequestOptions extends Omit<RequestInit, "body" | "headers"> {
  body?: unknown;
  headers?: RequestHeaders;
}

export async function jsonRequest<T>(
  endpoint: string = "",
  options: RequestOptions = {},
) {
  const defaultHeaders = {
    "Content-Type": "application/json",
  };

  const config = {
    method: "GET",
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
  };
  const normalizedEndpoint = normalizeUrl(endpoint);

  try {
    const response = await fetch(endpoint, config);

    if (!response.ok) {
      const contentType = response.headers.get("content-type");
      const isJson = contentType?.includes("application/json");

      let errorMessage = "";

      if (isJson) {
        const jsonError = await response.json();
        errorMessage =
          jsonError.error || jsonError.message || JSON.stringify(jsonError);
      } else {
        const textError = await response.text();
        errorMessage = textError || response.statusText;
      }

      const status = response.status;

      let error: HttpError | undefined;

      if (status >= 400 && status < 500) {
        // 400-499 errors
        error = new RequestError(
          `[${normalizedEndpoint}] - ${errorMessage}`,
          endpoint,
          config.method,
          status,
          errorMessage,
        );
      }

      if (status >= 500 && status < 600) {
        // 500-599 errors
        error = new ResponseError(
          `[${normalizedEndpoint}] - ${errorMessage}`,
          endpoint,
          config.method,
          status,
          errorMessage,
        );
      }

      if (error) {
        reportErrorToSentry({
          error,
          endpoint,
          normalizedEndpoint,
          method: config.method,
          errorType: error.name,
          status,
          errorMessage,
        });

        throw error;
      }
    }

    const result = response.headers
      .get("content-type")
      ?.includes("application/json")
      ? await response.json()
      : await response.text();

    return result as T;
  } catch (err) {
    // If error is already one of our HTTP error types, just rethrow it
    if (err instanceof HttpError) {
      throw err;
    }

    // Handle network-related errors - typically TypeErrors in fetch API
    if (err instanceof TypeError) {
      const errorMessage = err.message;
      const networkError = new NetworkError(
        `[${normalizedEndpoint}] - ${errorMessage}`,
        err,
        { endpoint, method: config.method },
      );

      reportErrorToSentry({
        error: networkError,
        endpoint,
        normalizedEndpoint,
        method: config.method,
        errorType: "NetworkError",
      });

      throw networkError;
    }

    // Unknown errors
    const unknownError = new UnknownError(
      err instanceof Error ? err : new Error(String(err)),
      endpoint,
      config.method,
    );

    reportErrorToSentry({
      error: unknownError,
      endpoint,
      normalizedEndpoint,
      method: config.method,
      errorType: "UnknownError",
    });

    throw unknownError;
  }
}
